import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuthData, extractAuthFromUrl, clearAuthData } from '../utils/authCookieHelpers';
import { APP_CONFIG } from '../configs/config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      try {
        // Get auth data from cookies or localStorage
        const authData = getAuthData();
        
        // If no auth data found, just clear local state (not cookies!)
        // Don't call clearAuthData() here as it would clear cookies from main website
        if (!authData.isAuthenticated) {
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
          return;
        }
        
        setToken(authData.token);
        setUser(authData.user);
        setIsAuthenticated(authData.isAuthenticated);
      } catch (error) {
        console.error('[AuthContext] Error checking auth:', error);
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    // Extract auth from URL parameters on mount (for IP-based deployments)
    extractAuthFromUrl();

    // Check auth on mount
    checkAuth();

    // Re-check periodically (in case user logs out in another tab)
    const interval = setInterval(checkAuth, 2000);
    
    // Listen for storage changes from other tabs/windows
    // This helps detect when user logs out from main website
    const handleStorageChange = (e) => {
      // If auth-related storage was cleared or changed
      // Watch for both our keys and main website's keys
      if (e.key === 'access_token' || 
          e.key === 'user_data' || 
          e.key === 'token' || // Main website uses 'token'
          e.key === null) { // null means clear() was called
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const logout = () => {
    // Clear both cookies and localStorage
    clearAuthData();
    
    // Clear local state
    setUser(null);
    setIsAuthenticated(false);
    setToken(null);
    
    // Redirect to main website logout
    window.location.href = `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}/signout`;
  };

  const redirectToSignIn = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    const signInUrl = `${APP_CONFIG.MAIN_WEBSITE_ORIGIN}/signin?returnUrl=${returnUrl}`;
    
    window.location.href = signInUrl;
  };

  const value = { 
    user, 
    isAuthenticated, 
    isLoading, 
    token,
    logout,
    redirectToSignIn
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};


